import {
    ComponentProps,
    ForwardedRef,
    ReactNode,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DropdownInput from "common/blocks/DropdownInput";
import MarketingConsent from "common/blocks/MarketingConsent";
import TextInput from "common/blocks/TextInput";
import useKeysValidation from "hooks/useKeysValidation/useKeysValidation";
import { FieldValues, useForm } from "react-hook-form";
import { ResolvedImage } from "resolvers/data/imageResolver";
import { ZodTypeAny, z } from "zod";

import Checkbox from "../Checkbox";
import DefaultErrorView from "../TextInput/DefaultErrorView";
import styles from "./DynamicForm.module.scss";

const cx = classNames.bind(styles);

type CommonComponentProps = {
    validationRule: z.ZodType;
    name: string;
    label: string;
    regex?: string | RegExp;
    helperText?: string;
} & ComponentProps<"input">;

type InputComponentProps = CommonComponentProps & { fieldType: "input" };

type TextAreaComponentProps = CommonComponentProps & {
    fieldType: "textarea";
};

type CheckboxComponentProps = CommonComponentProps & {
    fieldType: "checkbox";
};

type SelectComponentProps = CommonComponentProps & {
    fieldType: "select";
    selectOptions: { label: string; value: string }[];
};

type MarketingComponentProps = {
    fieldType: "marketingconsent";
    name: string;
    primaryText: string;
    secondaryText?: string;
    image?: ResolvedImage;
    validationRule: z.ZodType;
};

export type DynamicFormProps<T, S extends ZodTypeAny> = {
    fieldData: (
        | InputComponentProps
        | TextAreaComponentProps
        | SelectComponentProps
        | CheckboxComponentProps
        | MarketingComponentProps
    )[];
    onSubmit: (values: T) => Promise<void>;
    onSubmitSuccessChange?: (isSubmitSuccess: boolean) => void;
    submitButtonCopy: string;
    schema: S;
};

export type DynamicFormRef = {
    reset: () => void;
    setRecaptchaToken: (recaptchaToken: string) => void;
    scrollIntoView: () => void;
    setError: (fieldName: string, error: { message: string }) => void;
    setFormVisible: (visible: boolean) => void;
};

function DynamicForm<T, S extends ZodTypeAny>(
    {
        schema,
        children,
        fieldData,
        onSubmit,
        submitButtonCopy,
        onSubmitSuccessChange,
    }: DynamicFormProps<T, S> & { children: ReactNode },
    ref: ForwardedRef<DynamicFormRef>
) {
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, isSubmitSuccessful },
        reset,
        setValue,
        setError,
        trigger,
        getValues,
    } = useForm({
        resolver: zodResolver(schema),
        mode: "onBlur",
    });

    const recaptchaTokenRef = useRef<HTMLInputElement>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);
    const formErrorMessage =
        "Sorry, we were unable to complete your request this time, please try again later";

    useEffect(() => {
        const loadRecaptchaScript = async () => {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`;
            script.async = true;
            script.defer = true;

            document.head.appendChild(script);

            return () => {
                document.head.removeChild(script);
            };
        };

        loadRecaptchaScript();
    }, []);

    useEffect(() => {
        if (onSubmitSuccessChange) {
            onSubmitSuccessChange(isSubmitSuccessful);
            reset();
        } else {
            setError("root", {
                message: formErrorMessage,
            });
        }
    }, [isSubmitSuccessful]);

    const [isFormVisible, setFormVisible] = useState(true);

    useImperativeHandle(ref, () => ({
        reset,
        setRecaptchaToken: (recaptchaToken: string) => {
            if (recaptchaTokenRef.current) {
                recaptchaTokenRef.current.value = recaptchaToken;
            }
        },
        scrollIntoView: () => {
            if (formContainerRef.current) {
                formContainerRef.current.scrollIntoView({ behavior: "smooth" });
            }
        },
        setError,
        setFormVisible,
    }));

    const { handleKeyDownEvent, handlePasteEvent } = useKeysValidation();

    const onSubmitHandler = async (values: FieldValues) => {
        await onSubmit(values as T);
    };
    const renderField = (field: DynamicFormProps<T, S>["fieldData"][number]) => {
        switch (field.fieldType) {
            case "input": {
                return (
                    <div className={cx("textInput")}>
                        <TextInput
                            className={cx(`${field.type}Input`)}
                            {...register(field.name)}
                            type={field.type}
                            label={field.label}
                            autoComplete={field.autoComplete}
                            minLength={field.minLength}
                            maxLength={field.maxLength}
                            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                if (field.regex) {
                                    handleKeyDownEvent(event, field.regex);
                                }
                            }}
                            onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
                                if (field.regex) {
                                    handlePasteEvent(event, field.regex);
                                }
                            }}
                            helperText={field.helperText}
                            hasError={!!errors[field.name]?.message}
                            errorText={errors[field.name]?.message?.toString()}
                            inputMode={field.inputMode}
                            pattern={field.pattern}
                        />
                    </div>
                );
            }
            case "textarea": {
                return (
                    <div className={cx("textInput")}>
                        <TextInput
                            className={cx(`${field.type}Input`, "messageInput")}
                            {...register(field.name)}
                            type={field.type}
                            isTextareaField
                            label={field.label}
                            helperText={field.helperText}
                            aria-invalid={errors.message ? "true" : "false"}
                            hasError={!!errors[field.name]?.message}
                            errorText={errors[field.name]?.message?.toString()}
                        />
                    </div>
                );
            }
            case "checkbox": {
                return (
                    <div className={cx("checkboxInput")}>
                        <Checkbox
                            className={cx(`${field.type}Input`, "messageInput")}
                            {...register(field.name)}
                            type={field.type}
                            label={field.label}
                            aria-invalid={errors.message ? "true" : "false"}
                        />
                    </div>
                );
            }
            case "select": {
                return (
                    <div className={cx("dropdownInput")}>
                        <DropdownInput
                            containerClassName={cx(field.name)}
                            {...register(field.name)}
                            options={field.selectOptions}
                            onChange={(newOption) => {
                                setValue(field.name, newOption.value), trigger(field.name);
                            }}
                            value={{ label: getValues(field.name), value: getValues(field.name) }}
                            label={field.label}
                            hasError={!!errors[field.name]?.message}
                            errorText={errors[field.name]?.message?.toString()}
                            showCustomErrorView
                            onBlur={() => trigger(field.name)}
                        />
                    </div>
                );
            }
            case "marketingconsent": {
                return (
                    <MarketingConsent
                        primaryText={field.primaryText}
                        secondaryText={field.secondaryText}
                        image={field.image}
                        register={register}
                    />
                );
            }
            default: {
                null;
            }
        }
        return null;
    };

    return (
        <div
            className={cx("dynamic-form", { "dynamic-form--isVisible": isFormVisible })}
            ref={formContainerRef}
        >
            <form className={cx("dynamic-form--info")} onSubmit={handleSubmit(onSubmitHandler)}>
                {fieldData.map((field) => renderField(field))}

                <input type="hidden" ref={recaptchaTokenRef} name="recaptchaToken" />

                {!!errors.root && (
                    <DefaultErrorView
                        errorClassName={cx("globalError")}
                        errorTextClassName={cx("errorText")}
                        errorIconClassName={cx("errorIcon")}
                        errorText={errors.root.message}
                    />
                )}
                {children}
                <div>
                    <Button
                        className={cx("dynamic-form--info-button")}
                        theme="primary"
                        type="submit"
                        ariaLabel={submitButtonCopy}
                        isDisabled={isSubmitting}
                        autoWidth
                        loading={isSubmitting}
                    >
                        {submitButtonCopy}
                    </Button>
                </div>
            </form>
        </div>
    );
}

const ForwardedDynamicForm = forwardRef(DynamicForm) as unknown as <T, S extends ZodTypeAny>(
    props: DynamicFormProps<T, S> & { ref?: ForwardedRef<DynamicFormRef> }
) => JSX.Element;

export default ForwardedDynamicForm;
